/**
 * SEARCH-E4：Journey keyword combobox（draft suggestions + keyboard / IME / a11y）。
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { SUGGESTION_MIN_CODE_POINTS } from './useDiscoverySuggestions';

function codePointLength(value) {
  return Array.from(String(value ?? '')).length;
}

function optionId(listId, index) {
  return `${listId}-opt-${index}`;
}

const EMPTY_SUGGESTION_ITEMS = [];

export default function JourneySearchCombobox({
  t,
  suggestions,
  onSubmitQuery,
  onSelectSuggestion,
  onClearKeyword,
}) {
  const listId = useId();
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const blurCloseTimer = useRef(null);

  const items = useMemo(() => suggestions.items || EMPTY_SUGGESTION_ITEMS, [suggestions.items]);
  const composing = Boolean(suggestions.composing);
  const draft = suggestions.query || '';
  const canSuggest = codePointLength(draft.trim()) >= SUGGESTION_MIN_CODE_POINTS;
  const showList = open && canSuggest && (
    suggestions.phase === 'loading'
    || suggestions.phase === 'ready'
    || suggestions.phase === 'error'
    || items.length > 0
  );

  useEffect(() => {
    // 列表更新時 clamp／重置 active，避免 aria-activedescendant 指到不存在的 option
    setActiveIndex((current) => {
      if (!showList || items.length === 0) return -1;
      if (current < 0) return current;
      return Math.min(current, items.length - 1);
    });
  }, [items, showList]);

  useEffect(() => () => {
    if (blurCloseTimer.current) clearTimeout(blurCloseTimer.current);
  }, []);

  function cancelBlurClose() {
    if (blurCloseTimer.current) {
      clearTimeout(blurCloseTimer.current);
      blurCloseTimer.current = null;
    }
  }

  function closeMenu() {
    setOpen(false);
    setActiveIndex(-1);
  }

  function selectIndex(index) {
    const item = items[index];
    if (!item) return;
    closeMenu();
    suggestions.resetSuggestionResults?.();
    onSelectSuggestion?.(item);
  }

  function submitDraft() {
    closeMenu();
    suggestions.resetSuggestionResults?.();
    onSubmitQuery?.(draft);
  }

  function handleKeyDown(event) {
    if (composing || event.nativeEvent?.isComposing || event.keyCode === 229) {
      // IME composition 期間 Enter 不得 submit／選 suggestion
      return;
    }
    switch (event.key) {
      case 'ArrowDown': {
        if (!canSuggest) return;
        event.preventDefault();
        setOpen(true);
        setActiveIndex((current) => {
          if (items.length === 0) return -1;
          return current < 0 ? 0 : Math.min(current + 1, items.length - 1);
        });
        break;
      }
      case 'ArrowUp': {
        if (!canSuggest) return;
        event.preventDefault();
        setOpen(true);
        setActiveIndex((current) => {
          if (items.length === 0) return -1;
          return current <= 0 ? items.length - 1 : current - 1;
        });
        break;
      }
      case 'Enter': {
        event.preventDefault();
        if (showList && activeIndex >= 0 && items[activeIndex]) {
          selectIndex(activeIndex);
        } else {
          submitDraft();
        }
        break;
      }
      case 'Escape': {
        if (showList) {
          event.preventDefault();
          closeMenu();
        }
        break;
      }
      default:
        break;
    }
  }

  const activeDescendant = showList && activeIndex >= 0
    ? optionId(listId, activeIndex)
    : undefined;

  return (
    <div className="discover-filters__suggest">
      <label className="discover-filters__field" htmlFor={`${listId}-input`}>
        <span>{t('travel.discover.search.label') || 'Search journeys'}</span>
        <div className="discover-filters__suggest-row">
          <input
            ref={inputRef}
            id={`${listId}-input`}
            type="search"
            role="combobox"
            className="discover-filters__input"
            value={draft}
            onChange={(e) => {
              suggestions.onQueryChange(e.target.value);
              setOpen(true);
              setActiveIndex(-1);
            }}
            onCompositionStart={suggestions.onCompositionStart}
            onCompositionEnd={(e) => {
              suggestions.onCompositionEnd(e.target.value);
              setOpen(true);
            }}
            onFocus={() => {
              cancelBlurClose();
              if (canSuggest) setOpen(true);
            }}
            onBlur={() => {
              // 延遲關閉，讓 option mousedown／click 先完成（避免 blur 搶先關掉）
              blurCloseTimer.current = setTimeout(() => closeMenu(), 120);
            }}
            onKeyDown={handleKeyDown}
            placeholder={t('travel.discover.search.placeholder') || '東京、鎌倉、Tokyo…'}
            aria-label={t('travel.discover.search.label') || 'Search journeys'}
            aria-autocomplete="list"
            aria-expanded={showList}
            aria-controls={listId}
            aria-activedescendant={activeDescendant}
            autoComplete="off"
            data-testid="journey-search-input"
          />
          <button
            type="button"
            className="discover-filters__search-btn"
            onClick={submitDraft}
            data-testid="journey-search-submit"
          >
            {t('travel.discover.search.submit') || 'Search'}
          </button>
          {draft ? (
            <button
              type="button"
              className="discover-filters__clear-search"
              onClick={() => {
                closeMenu();
                onClearKeyword?.();
              }}
              data-testid="journey-search-clear"
            >
              {t('travel.discover.search.clear') || 'Clear'}
            </button>
          ) : null}
        </div>
      </label>

      {showList ? (
        <ul
          id={listId}
          className="discover-filters__suggest-list"
          role="listbox"
          data-testid="discovery-suggestions"
        >
          {suggestions.phase === 'loading' ? (
            <li className="discover-filters__suggest-status" role="presentation">
              {t('travel.discover.suggestions.loading') || 'Loading suggestions…'}
            </li>
          ) : null}
          {suggestions.phase === 'error' ? (
            <li className="discover-filters__suggest-status" role="presentation">
              {t('travel.discover.suggestions.error') || 'Suggestions unavailable. Press Enter to search.'}
            </li>
          ) : null}
          {suggestions.phase === 'ready' && items.length === 0 ? (
            <li className="discover-filters__suggest-status" role="presentation">
              {t('travel.discover.suggestions.empty') || 'No suggestions. Press Enter to search.'}
            </li>
          ) : null}
          {items.map((item, index) => {
            const selected = index === activeIndex;
            const typeLabel = item.type === 'GEO'
              ? (t('travel.discover.suggestions.type.geo') || 'Location')
              : (t('travel.discover.suggestions.type.journey') || 'Journey');
            return (
              <li
                key={`${item.type}:${item.id}`}
                id={optionId(listId, index)}
                role="option"
                aria-selected={selected}
                aria-label={`${typeLabel}: ${item.label}`}
                className={`discover-filters__suggest-option${selected ? ' is-active' : ''}`}
                onMouseDown={(e) => {
                  // 防止 input blur 先關掉 list
                  e.preventDefault();
                }}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectIndex(index)}
              >
                <span className="discover-filters__suggest-type">{typeLabel}</span>
                <span className="discover-filters__suggest-label">{item.label}</span>
                {item.secondaryLabel ? (
                  <span className="discover-filters__suggest-secondary">{item.secondaryLabel}</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
